import { useLayoutEffect, useRef, useState } from 'react';
import {
  PIPELINE_STEPS,
  getPipelineIndex,
  getStepState,
} from '../lib/matterPipeline.js';

function StageInfoTip({ text, label }) {
  const [show, setShow] = useState(false);
  const [coords, setCoords] = useState(null);
  const btnRef = useRef(null);

  useLayoutEffect(() => {
    if (!show || !text) {
      setCoords(null);
      return undefined;
    }

    function placeBubble() {
      const el = btnRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const margin = 10;
      const width = Math.min(240, window.innerWidth - margin * 2);
      const estimatedHeight = 88;
      const btnCenter = r.left + r.width / 2;
      let left = btnCenter - width / 2;
      left = Math.max(margin, Math.min(left, window.innerWidth - width - margin));
      const caretLeft = Math.max(14, Math.min(width - 14, btnCenter - left));

      let top = r.bottom + 8;
      let placement = 'below';
      if (top + estimatedHeight > window.innerHeight - margin) {
        top = Math.max(margin, r.top - estimatedHeight - 8);
        placement = 'above';
      }

      setCoords({ left, top, width, placement, caretLeft });
    }

    placeBubble();
    window.addEventListener('resize', placeBubble);
    window.addEventListener('scroll', placeBubble, true);
    return () => {
      window.removeEventListener('resize', placeBubble);
      window.removeEventListener('scroll', placeBubble, true);
    };
  }, [show, text]);

  if (!text) return null;

  return (
    <span
      className="kanban-stage-tip stepper-stage-tip"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      onFocus={() => setShow(true)}
      onBlur={() => setShow(false)}
    >
      <button
        ref={btnRef}
        type="button"
        className="kanban-stage-tip-btn"
        aria-label={`About ${label}: ${text}`}
        tabIndex={0}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setShow(true);
        }}
      >
        <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden>
          <circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <path
            d="M8 7.2v3.6M8 5.2h.01"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>
      {show && coords ? (
        <span
          className={`stepper-stage-tip-bubble is-anchored is-${coords.placement}`}
          role="tooltip"
          style={{
            left: coords.left,
            top: coords.top,
            width: coords.width,
            ['--tip-caret-left']: `${coords.caretLeft}px`,
          }}
        >
          {text}
        </span>
      ) : null}
    </span>
  );
}

export default function StatusStepper({
  status,
  compact = false,
  showTooltips = !compact,
  className = '',
}) {
  const currentIndex = getPipelineIndex(status);

  return (
    <div
      className={`status-stepper ${compact ? 'is-compact' : 'is-full'} ${className}`.trim()}
      aria-label={`Matter progress: ${PIPELINE_STEPS[currentIndex]?.shortLabel || status}`}
    >
      <ol className="stepper-track">
        {PIPELINE_STEPS.map((step, index) => {
          const state = getStepState(index, currentIndex, status);
          const label = step.shortLabel || step.label;
          return (
            <li key={step.id} className={`stepper-step state-${state}`}>
              <span className="stepper-node" aria-hidden>
                {state === 'complete' ? (
                  <svg viewBox="0 0 16 16" width="12" height="12">
                    <path
                      d="M3.5 8.5l3 3 6-7"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <span className="stepper-dot" />
                )}
              </span>
              <span className="stepper-label">
                <span className="stepper-label-row">
                  {label}
                  {showTooltips ? <StageInfoTip text={step.tooltip} label={label} /> : null}
                </span>
                {state === 'current' ? <em>Current</em> : null}
              </span>
              {index < PIPELINE_STEPS.length - 1 ? (
                <span className={`stepper-connector state-${state}`} aria-hidden />
              ) : null}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
